import React, { useEffect, useState, useContext } from "react";
import {
  Box,
  Button,
  Modal,
  Typography,
  Grid,
  Autocomplete,
  Chip,
  Stack,
  Tooltip,
  IconButton,
} from "@mui/material";
import TextField from "@mui/material/TextField";
import {
  AssignmentLate,
  AssignmentTurnedIn,
  PendingActions,
  CloudDownload,
  DeleteOutline,
  Verified,
} from "@mui/icons-material";
import { useFormik } from "formik";
import * as Yup from "yup";
import * as Constants from "../../support/Constants";
import * as Web3Manager from "../../support/Web3Manager";
import { CancelButton } from "../ui/CustomButtons";
import SpinnerContext from "../../context/spinner/SpinnerContext";
import CustomTextEditor from "../ui/CustomTextEditor";
import { UploadDocumentsModal } from "../UploadDocumentsModal/UploadDocumentsModal";
import { useDocuments } from "../../hooks/useDocuments";
import Link from "@mui/material/Link";

import moment from "moment";
import { copyHash, shortHash } from "../utiles/hashUtils";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "50%",
  maxHeight: "70%",
  overflow: "auto",
  bgcolor: "background.paper",
  border: "1px solid #000",
  boxShadow: 24,
  p: 4,
};

export const ModalColaboracionesForm = ({
  open,
  handleClose,
  isEditing,
  colaboracionEditing,
  personas,
  empresas,
  principios,
  compromisos,
  categoriasColaboracion,
  setLoading,
  initData,
}) => {
  const [disableButton, setDisableButton] = useState(false);
  const [nombreReceptor, setNombreReceptor] = useState("");
  const [nombreAplicacion, setNombreAplicacion] = useState("");
  const [nombreCompromiso, setNombreCompromiso] = useState("");
  const [tipoSocio, setTipoSocio] = useState("");
  const [statusCompromiso, setStatusCompromiso] = useState("");
  const [deadlineCompromiso, setDeadlineCompromiso] = useState("");

  const [documents, setDocuments] = useState([]);

  const { getFilesBatch } = useDocuments();
  const { openSpinner } = useContext(SpinnerContext);
  const [copiedIndex, setCopiedIndex] = useState(null);

  const validationSchema = Yup.object({
    _description: Yup.string()
      .required("Debes ingresar una descripción para la colaboración")
      .test(
        "no-html-tags",
        "Debes ingresar una descripción para la colaboración",
        (value) => {
          const strippedString = value?.replace(/(<([^>]+)>)/gi, "");
          return strippedString && strippedString.trim().length > 0;
        }
      ),
    _category: Yup.string().required("Debes seleccionar una categoría"),
  });

  // Carga datos de cabecera + documentos al editar
  useEffect(() => {
    const fetchData = async () => {
      if (colaboracionEditing || initData) {
        const data = colaboracionEditing ? colaboracionEditing : initData;

        const socio =
          data.targetMemberType == Constants.MEMBER_TYPE_PERSONA
            ? await personas.find(
                (p) =>
                  p.memberAddress?.toLowerCase() ==
                  data.targetMemberAddress?.toLowerCase()
              )
            : await empresas.find(
                (e) =>
                  e.memberAddress?.toLowerCase() ==
                  data.targetMemberAddress?.toLowerCase()
              );
        setTipoSocio(getTipoSocio(data.targetMemberType));
        setNombreReceptor(socio.name);

        const compromiso = await compromisos.find(
          (c) => c.code === data.collaborationTypeCode
        );
        setNombreCompromiso(compromiso.title);

        let adhesion;
        if (data.applicationId && data.applicationId !== 0) {
          const aplicacion = await socio.services.find(
            (s) => s.code === data.applicationId
          );
          adhesion = await aplicacion.commitments.find(
            (c) => c.commitmentCode === data.collaborationTypeCode
          );
          setNombreAplicacion(aplicacion.name);
        } else {
          adhesion = await socio.commitments.find(
            (c) => c.commitmentCode === data.collaborationTypeCode
          );
        }

        if (adhesion) {
          setStatusCompromiso(
            Constants.obtenerNombreEstadoCompromiso(adhesion.status)
          );
          if (adhesion.status === Constants.HAS_INTENTION) {
            setDeadlineCompromiso(adhesion.deadlineDate);
          }
        } else {
          setStatusCompromiso("No adhiere");
        }
      }

      if (!isEditing || !colaboracionEditing?.documents?.length) {
        setDocuments([]);
        return;
      }

      const docsFromBackend = colaboracionEditing.documents;
      const fileIds = docsFromBackend
        .map((d) => Number(d.fileId ?? d.id))
        .filter((id) => !Number.isNaN(id));

      if (!fileIds.length) {
        setDocuments(docsFromBackend);
        return;
      }

      try {
        const files = await getFilesBatch(fileIds);

        const merged = docsFromBackend.map((doc, index) => {
          const storageFile = files[index];
          const id = doc.fileId ?? doc.id ?? fileIds[index];

          return {
            ...doc,
            id,
            fileId: id,
            name:
              storageFile?.name ||
              storageFile?.filename ||
              doc.name ||
              "Documento",
            url: storageFile?.url || doc.url,
            hash: doc.hash ?? storageFile?.hash ?? null,
          };
        });

        setDocuments(merged);
      } catch (e) {
        console.error("Error loading collaboration documents:", e);
        setDocuments(colaboracionEditing?.documents ?? []);
      }
    };

    fetchData().catch(console.error);
  }, []);

  const initialState = {
    _sourceMemberAddress: Web3Manager.MEMBER_DATA.memberAddress,
    _sourceMemberType: Web3Manager.MEMBER_DATA.memberType,
    _sourceMemberCode: Web3Manager.MEMBER_DATA.memberCode,
    _targetMemberAddress: initData ? initData.targetMemberAddress : "",
    _targetMemberCode: initData ? initData.targetMemberCode : "",
    _targetMemberType: initData
      ? initData.targetMemberType
      : Constants.MEMBER_TYPE_PERSONA,
    _title: "",
    _description: "",
    _category: "",
    _collaborationType: initData
      ? initData.collaborationType
      : Constants.COLLABORATION_COMMITMENT,
    _collaborationTypeCode: initData ? initData.collaborationTypeCode : 0,
    _applicationId: initData ? initData.applicationId : 0,
  };

  const getFormData = () => {
    let formData;

    if (!isEditing) {
      formData = initialState;
    } else {
      formData = {
        _code: colaboracionEditing.code,
        _sourceMemberAddress: colaboracionEditing.sourceMemberAddress,
        _sourceMemberCode: colaboracionEditing.sourceMemberCode,
        _sourceMemberType: colaboracionEditing.sourceMemberType,
        _targetMemberAddress: colaboracionEditing.targetMemberAddress,
        _targetMemberCode: colaboracionEditing.targetMemberCode,
        _targetMemberType: colaboracionEditing.targetMemberType,
        _title: colaboracionEditing.title,
        _description: colaboracionEditing.description,
        _category: colaboracionEditing.category,
        _collaborationType: colaboracionEditing.collaborationType,
        _collaborationTypeCode: colaboracionEditing.collaborationTypeCode,
        _applicationId: colaboracionEditing.applicationId,
      };
    }
    return formData;
  };

  const formColaboracion = useFormik({
    initialValues: getFormData(),
    validateOnChange: true,
    enableReinitialize: true,
    validationSchema: validationSchema,
    onSubmit: (values, { resetForm }) => {
      const {
        _sourceMemberAddress,
        _sourceMemberCode,
        _sourceMemberType,
        _targetMemberAddress,
        _targetMemberCode,
        _targetMemberType,
        _description,
        _category,
        _collaborationType,
        _collaborationTypeCode,
        _applicationId,
      } = values;

      let _title;
      if (_collaborationType == Constants.COLLABORATION_PRINCIPLE) {
        const principioSeleccionado = principios?.find(
          (p) => p?.code == values._collaborationTypeCode
        );
        _title = principioSeleccionado.title;
      } else {
        const compromisoSeleccionado = compromisos?.find(
          (c) => c?.code == values._collaborationTypeCode
        );
        _title = compromisoSeleccionado.title;
      }

      // Mapear documentos al struct del contrato: { fileId, hash }
      const collaborationDocuments = documents
        .map((d) => {
          const fileId = d.fileId ?? d.id;
          if (!fileId) return null;

          return {
            hash: d.hash ?? "",
            fileId,
          };
        })
        .filter(Boolean);

      const hasDocuments = collaborationDocuments.length > 0;

      const methodName = !isEditing
        ? hasDocuments
          ? "createAndActivateCollaborationWithDocuments"
          : "createAndActivateCollaboration"
        : hasDocuments
        ? "modifyCollaborationWithDocuments"
        : "modifyCollaboration";

      setDisableButton(true);
      setLoading(true);
      openSpinner(true);

      const baseArgs = !isEditing
        ? [
            _sourceMemberAddress,
            _sourceMemberCode,
            _sourceMemberType,
            _targetMemberAddress,
            _targetMemberCode,
            _targetMemberType,
            _title,
            _description,
            _category,
            _collaborationType,
            _collaborationTypeCode,
            _applicationId,
          ]
        : [
            colaboracionEditing.code,
            _sourceMemberAddress,
            _sourceMemberCode,
            _sourceMemberType,
            _targetMemberAddress,
            _targetMemberCode,
            _targetMemberType,
            _title,
            _description,
            _category,
            _collaborationType,
            _collaborationTypeCode,
            _applicationId,
          ];

      const finalArgs = hasDocuments ? [...baseArgs, collaborationDocuments] : baseArgs;

      Web3Manager.callContract(
        Web3Manager.CONTRACT_COLLABORATIONS,
        methodName,
        ...finalArgs
      )
        .then((response) => {
          setDisableButton(false);
          resetForm();
          handleClose(false, response, true);
        })
        .catch((error) => {
          setDisableButton(false);
          setLoading(false);
          handleClose(false, error, false);
        });
    },
  });

  const {
    handleSubmit,
    handleChange,
    handleBlur,
    touched,
    errors,
    values,
    resetForm,
  } = formColaboracion;

  const getTipoSocio = (tipo) => {
    if (tipo === Constants.MEMBER_TYPE_PERSONA) {
      return "Persona";
    } else if (tipo === Constants.MEMBER_TYPE_EMPRESA) {
      return "Empresa";
    } else {
      return "N/A";
    }
  };

  const getChipInfo = (status) => {
    let icon;
    let label;
    if (status === "Cumple") {
      icon = <AssignmentTurnedIn style={{ color: "green" }} />;
      label = "Cumple";
    } else if (status === "Tiene intención") {
      icon = <PendingActions style={{ color: "orange" }} />;
      label = "Tiene intención";
    } else if (status === "No adhiere") {
      icon = <AssignmentLate style={{ color: "red" }} />;
      label = "Ya no adhiere";
    } else {
      icon = null;
      label = "Desconocido";
    }

    return (
      <Chip
        sx={{ p: 2, ml: 1 }}
        icon={icon}
        label={label}
        variant="outlined"
        size="small"
      />
    );
  };

  const getDeadlineDate = (date) => {
    return <strong>{moment(date).format("DD-MM-YYYY")}</strong>;
  };

  const onUploadDocumentsSuccess = (newDocs = []) => {
    const mapped = newDocs.map((d) => ({
      ...d,
      id: d.id,
      fileId: d.id,
      name: d.filename || d.name || "Documento",
    }));
    setDocuments((prev) => [...prev, ...mapped]);
  };

  const handleDeleteDoc = async (docId) => {
    try {
      setDocuments((prev) => prev.filter((d) => d.id !== docId));
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <Modal
      open={open}
      onClose={() => {
        resetForm();
        handleClose(true, null, false);
      }}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Box sx={style} component="form" onSubmit={handleSubmit}>
        <Typography id="modal-modal-title" variant="h6" component="h2">
          {colaboracionEditing ? "Modificar" : "Nueva"} {" colaboración "}
          {nombreReceptor ? "con " + nombreReceptor : ""}
        </Typography>

        <Grid
          sx={{
            bgcolor: "#F5F5F5",
            "& svg": {
              fontSize: {
                lg: "1.5rem",
                xs: "1.25rem",
              },
            },
            "& .MuiTypography-root": {
              fontSize: {
                lg: "0.875rem",
                xs: "0.8rem",
              },
            },
          }}
          container
          spacing={1}
          p={0.5}
          mt={4}
          mb={2}
          justifyContent="space-between"
        >
          <Grid item>
            {nombreCompromiso && (
              <Typography variant="body2" sx={{ p: 1 }}>
                COMPROMISO: <strong>{nombreCompromiso}</strong>
                <br />
              </Typography>
            )}
            <Typography variant="body2" sx={{ p: 1 }}>
              TIPO SOCIO: <strong>{tipoSocio}</strong>
              <br />
            </Typography>
            {nombreAplicacion && (
              <Typography variant="body2" sx={{ p: 1 }}>
                APLICACIÓN: <strong>{nombreAplicacion}</strong>
                <br />
              </Typography>
            )}
            <Typography variant="body2" sx={{ p: 1 }}>
              ADHESIÓN:
              {getChipInfo(statusCompromiso)}
              {deadlineCompromiso && (
                <> ● {getDeadlineDate(deadlineCompromiso)}</>
              )}
              <br />
            </Typography>
          </Grid>
        </Grid>

        <Box mt={2}>
          <Typography variant="subtitle1" fontWeight="bold">
            Elige la categoría
          </Typography>
          <br />
          <Grid container alignItems="center" spacing={2}>
            <Grid item sx={{ width: "100%" }}>
              <Autocomplete
                options={categoriasColaboracion}
                name="_category"
                getOptionLabel={(option) => option && `${option.category}`}
                defaultValue={
                  colaboracionEditing
                    ? categoriasColaboracion.find(
                        (c) => c.category === colaboracionEditing.category
                      )
                    : null
                }
                onChange={(event, value) => {
                  formColaboracion.setFieldValue(
                    "_category",
                    value ? value.category : null
                  );
                }}
                onBlur={handleBlur}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Categoría"
                    variant="outlined"
                    size="small"
                    fullWidth={true}
                    error={Boolean(touched._category && errors._category)}
                    helperText={touched._category && errors._category}
                  />
                )}
              />
            </Grid>
          </Grid>
        </Box>
        <Box mt={2} sx={{ borderRadius: "4px" }}>
          <Typography variant="subtitle1" fontWeight="bold">
            Agrega una descripción de tu colaboración
          </Typography>
          <br />
          <CustomTextEditor
            name="_description"
            placeholder="Descripción"
            value={values._description}
            handleChange={handleChange}
            handleBlur={handleBlur}
          />
        </Box>
        {touched._description && errors._description && (
          <Typography
            sx={{
              fontSize: "0.75rem",
              marginLeft: "14px",
              marginRight: "14px",
              marginTop: "4px",
            }}
            variant="body2"
            color="error"
          >
            {errors._description}
          </Typography>
        )}

        {/* Documentos */}
        <Box mt={3} mb={2}>
          <Stack direction="row" alignItems="center" gap={2}>
            <Typography variant="subtitle1" fontWeight={600}>
              Documentos
            </Typography>
            <UploadDocumentsModal
              openButton={
                <Button variant="contained" sx={{ textTransform: "none" }}>
                  Agregar documento
                </Button>
              }
              onSave={onUploadDocumentsSuccess}
            />
          </Stack>

          {documents.length > 0 ? (
            <Stack mt={2} gap={1}>
              {documents.map((doc, index) => (
                <Stack
                  key={doc.id ?? doc.url}
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{
                    border: "1px solid #e0e0e0",
                    borderRadius: "6px",
                    p: 1,
                  }}
                >
                  <Stack direction="row" alignItems="center" gap={1}>
                    <Typography fontWeight={500}>
                      {doc.name || doc.fileName || "Documento"}
                    </Typography>

                    {doc.hash && (
                      <Tooltip
                        title={
                          copiedIndex === index
                            ? "¡Copiado!"
                            : `SHA-256: ${doc.hash}`
                        }
                      >
                        <Chip
                          size="small"
                          label={
                            copiedIndex === index
                              ? "¡Copiado!"
                              : `SHA-256: ${shortHash(doc.hash)}`
                          }
                          variant="outlined"
                          onClick={() =>
                            copyHash(doc.hash, setCopiedIndex, index)
                          }
                          sx={{ cursor: "pointer" }}
                        />
                      </Tooltip>
                    )}

                    {doc.txHash && (
                      <Tooltip title="Verificado en blockchain">
                        <Verified fontSize="small" color="success" />
                      </Tooltip>
                    )}
                  </Stack>

                  <Stack direction="row" gap={1}>
                    {doc.url && (
                      <Tooltip title="Descargar">
                        <IconButton
                          component={Link}
                          href={doc.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          size="small"
                        >
                          <CloudDownload fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}

                    <Tooltip title="Eliminar">
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteDoc(doc.id)}
                      >
                        <DeleteOutline fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Stack>
              ))}
            </Stack>
          ) : (
            <Typography mt={1} color="text.secondary">
              Aún no hay documentos adjuntos
            </Typography>
          )}
        </Box>

        <br />
        <Box
          mt={2}
          sx={{ justifyContent: "left", display: "flex", gap: "10px" }}
        >
          <Button
            variant="contained"
            color="primary"
            type="submit"
            disabled={disableButton}
          >
            Confirmar
          </Button>
          <CancelButton
            label="Cancelar"
            onClick={() => {
              handleClose(true, null, false);
              resetForm();
            }}
          />
        </Box>
      </Box>
    </Modal>
  );
};
